import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Building, Check, AlertCircle, Save, Shield, Users } from 'lucide-react';
import permissionService from 'features/administrativo/services/permissionService';
import NotificationToast from 'components/common/NotificationToast';
import { useAuth } from 'contexts/AuthContext';

const UserCompanyManagement = ({ user, onClose, onUpdate }) => {
  const { refreshUserData } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [currentAccessLevel, setCurrentAccessLevel] = useState(user?.access_level || 'company_manager');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(user?.group?.id || null);
  
  // Estados para renderização incremental
  const [progressoCarregamento, setProgressoCarregamento] = useState({
    companies: false,
    groups: false
  });
  const [loadingIncremental, setLoadingIncremental] = useState(false);
  

  // Garantir que companies seja sempre um array - memoizado para evitar re-renders desnecessários
  const safeCompanies = useMemo(() => {
    return Array.isArray(companies) ? companies : [];
  }, [companies]);

  // Carregar empresas - definido antes do useEffect para evitar warning de dependências
  const loadCompanies = useCallback(async (isIncremental = false) => {
    try {
      const response = await permissionService.getManageableCompanies();
      
      // Verificar se a resposta tem a estrutura { success: true, data: { companies: [...] } }
      if (response && response.success && response.data && Array.isArray(response.data.companies)) {
        setCompanies(response.data.companies);
      } else if (response && Array.isArray(response)) {
        // Fallback para resposta direta em array
        setCompanies(response);
      } else {
        setCompanies([]);
      }
      
      if (isIncremental) {
        setProgressoCarregamento(prev => ({ ...prev, companies: true }));
      }
    } catch (error) {
      console.error('❌ Erro ao carregar empresas:', error);
      
      // Verificar se é erro de permissão
      if (error.response?.status === 403) {
        console.error('❌ Erro de permissão - Usuário não tem solicitacoes.manage');
        setNotification({
          type: 'error',
          message: 'Você não tem permissão para gerenciar empresas. Contate o administrador.',
          duration: 10000
        });
      }
      
      setCompanies([]);
    }
  }, []);

  // Carregar grupos - definido antes do useEffect para evitar warning de dependências
  const loadGroups = useCallback(async (isIncremental = false) => {
    try {
      const response = await permissionService.getAvailableGroups();
      
      // Verificar se a resposta tem a estrutura { success: true, data: [...] }
      if (response && response.success && response.data && Array.isArray(response.data)) {
        setGroups(response.data);
      } else if (response && Array.isArray(response)) {
        // Fallback para resposta direta em array
        setGroups(response);
      } else {
        setGroups([]);
      }
      
      if (isIncremental) {
        setProgressoCarregamento(prev => ({ ...prev, groups: true }));
      }
    } catch (error) {
      console.error('❌ Erro ao carregar grupos:', error);
      
      // Verificar se é erro de permissão
      if (error.response?.status === 403) {
        console.error('❌ Erro de permissão - Usuário não tem solicitacoes.manage');
        setNotification({
          type: 'error',
          message: 'Você não tem permissão para gerenciar grupos. Contate o administrador.',
          duration: 10000
        });
      }
      
      setGroups([]);
    }
  }, []);

  // Carregar empresas e grupos disponíveis com renderização incremental
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setLoadingIncremental(true);
      
      // Resetar progresso
      setProgressoCarregamento({
        companies: false,
        groups: false
      });
      
      try {
        
        // Carregar empresas primeiro (mais importante)
        await loadCompanies(true);
        
        // Carregar grupos em paralelo
        loadGroups(true);
        
      } catch (error) {
        console.error('❌ Erro ao carregar dados:', error);
      } finally {
        // Finalizar loading após um delay para permitir renderização incremental
        setTimeout(() => {
          setLoading(false);
          setLoadingIncremental(false);
        }, 200);
      }
    };
    loadData();
  }, [loadCompanies, loadGroups]);

  // Monitorar progresso da renderização incremental
  useEffect(() => {
    if (loadingIncremental) {
      const todasCompletas = Object.values(progressoCarregamento).every(completo => completo);
      if (todasCompletas) {
        setLoadingIncremental(false);
        setLoading(false);
      }
    }
  }, [progressoCarregamento, loadingIncremental]);

  // Selecionar todas as empresas se for gestor de grupo
  useEffect(() => {
    if (user && user.access_level === 'group_manager' && safeCompanies.length > 0) {
      setSelectedCompanies(safeCompanies.map(company => company.id));
    }
  }, [user, safeCompanies]);

  // Carregar empresas do usuário
  useEffect(() => {
    if (user) {
      if (user.access_level === 'group_manager' && user.group) {
        // Gestor de grupo: selecionar todas as empresas disponíveis
        setSelectedCompanies(safeCompanies.map(company => company.id));
      } else if (user.companies && Array.isArray(user.companies)) {
        // Usuário normal: selecionar empresas específicas
        setSelectedCompanies(user.companies.map(company => company.id));
      } else {
        setSelectedCompanies([]);
      }
      
      setCurrentAccessLevel(user.access_level || 'company_manager');
      setSelectedGroup(user.group?.id || null);
    }
  }, [user, safeCompanies]);

  const handleGroupChange = (groupId) => {
    setSelectedGroup(groupId);
    
    if (groupId) {
      // Se selecionou um grupo, pode selecionar múltiplas empresas
      setCurrentAccessLevel('company_manager');
    } else {
      // Se não tem grupo, só pode selecionar uma empresa
      setCurrentAccessLevel('company_manager');
      setSelectedCompanies([]);
    }
  };

  const handleAccessLevelChange = (level) => {
    setCurrentAccessLevel(level);
    
    if (level === 'group_manager') {
      // Gestor de grupo: selecionar todas as empresas
      setSelectedCompanies(safeCompanies.map(company => company.id));
    } else {
      // Outros níveis: limpar seleção
      setSelectedCompanies([]);
    }
  };

  const handleCompanyToggle = (companyId) => {
    
    // Se não tem grupo, só pode selecionar uma empresa
    if (!selectedGroup && selectedCompanies.length >= 1 && !selectedCompanies.includes(companyId)) {
      return;
    }
    
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const isCompanySelected = (companyId) => {
    return selectedCompanies.includes(companyId);
  };

  const getCompanyColor = (index) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500'];
    return colors[index % colors.length];
  };

  const handleSave = async () => {
    if (saving) return;
    
    setSaving(true);

    try {
      // 1. Atualizar nível de acesso
      if (currentAccessLevel !== user?.access_level) {
        await permissionService.updateUserAccessLevel(user.id, currentAccessLevel);
      }

      // 2. Atribuir grupo se necessário
      if (selectedGroup !== user?.group?.id) {
        await permissionService.assignUserToGroup(user.id, selectedGroup);
      }

      // 3. Associar empresas
      const companyIds = selectedCompanies.filter(id => typeof id === 'number');
      await permissionService.assignUserToCompanies(user.id, companyIds);

      // 4. Atualizar dados do usuário no contexto
      if (refreshUserData) {
        await refreshUserData();
      }

      // 5. Atualizar lista de usuários
      if (onUpdate) {
        onUpdate();
      }

      setNotification({
        message: 'Configurações salvas com sucesso!',
        type: 'success'
      });

      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('❌ Erro ao salvar configurações:', error);
      setNotification({
        message: `Erro ao salvar: ${error.response?.data?.message || error.message}`,
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    // Mostrar progresso incremental se disponível
    if (loadingIncremental && progressoCarregamento) {
      const progresso = Object.values(progressoCarregamento).filter(Boolean).length;
      const total = Object.keys(progressoCarregamento).length;
      const percentual = Math.round((progresso / total) * 100);
      
      return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto theme-surface border" style={{ borderColor: 'var(--border)' }}>
            <div className="p-6">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-lg font-medium theme-text mb-2">
                    Carregando dados do usuário...
                  </div>
                  <div className="w-full rounded-full h-2 mb-2 theme-surface-muted">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentual}%` }}
                    ></div>
                  </div>
                  <div className="text-sm theme-text-muted">
                    {percentual}% - {progresso} de {total} seções carregadas
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className={`flex items-center ${progressoCarregamento.companies ? 'text-green-500' : 'theme-text-muted'}`}>
                    {progressoCarregamento.companies ? '✓' : '⏳'} Empresas
                  </div>
                  <div className={`flex items-center ${progressoCarregamento.groups ? 'text-green-500' : 'theme-text-muted'}`}>
                    {progressoCarregamento.groups ? '✓' : '⏳'} Grupos
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
        <div className="rounded-lg shadow-xl p-8 max-w-md w-full theme-surface border" style={{ borderColor: 'var(--border)' }}>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
            <h3 className="text-lg font-medium theme-text mb-2">Carregando empresas...</h3>
            <p className="text-sm theme-text-muted">Aguarde enquanto carregamos os dados...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-hidden">
        <div className="rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col theme-surface border" style={{ borderColor: 'var(--border)' }}>
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold theme-text">
                      Gerenciar Empresas
                    </h2>
                    <p className="text-sm theme-text-muted">
                      {user?.full_name} ({user?.corporate_email})
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg transition-colors hover:opacity-80"
                  style={{ background: 'var(--surface-muted)' }}
                >
                  <X className="h-5 w-5 theme-text-muted" />
                </button>
              </div>

              {/* Seleção de Grupo */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-5 w-5 theme-text-muted" />
                  <h3 className="text-lg font-medium theme-text">Grupo</h3>
                </div>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer theme-surface" style={{ borderColor: 'var(--border)' }}>
                    <input
                      type="radio"
                      name="group"
                      value=""
                      checked={!selectedGroup}
                      onChange={() => handleGroupChange(null)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="w-3 h-3 rounded-full theme-surface-muted"></div>
                    <div className="flex-1">
                      <div className="font-medium theme-text">Sem Grupo</div>
                      <div className="text-sm theme-text-muted">Acesso limitado a uma empresa</div>
                    </div>
                  </label>

                  {groups.map(group => (
                    <label key={group.id} className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer theme-surface" style={{ borderColor: 'var(--border)' }}>
                      <input
                        type="radio"
                        name="group"
                        value={group.id}
                        checked={selectedGroup === group.id}
                        onChange={() => handleGroupChange(group.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <div className="flex-1">
                        <div className="font-medium theme-text">{group.name}</div>
                        <div className="text-sm theme-text-muted">{group.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Escopo de Acesso */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-5 w-5 theme-text-muted" />
                  <h3 className="text-lg font-medium theme-text">Escopo de Acesso</h3>
                </div>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer theme-surface" style={{ borderColor: 'var(--border)' }}>
                    <input
                      type="radio"
                      name="accessLevel"
                      value="company_manager"
                      checked={currentAccessLevel === 'company_manager'}
                      onChange={() => handleAccessLevelChange('company_manager')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <div className="flex-1">
                      <div className="font-medium theme-text">Acesso por Empresa</div>
                      <div className="text-sm theme-text-muted">Acesso às empresas selecionadas</div>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${
                    !selectedGroup 
                      ? 'opacity-50 cursor-not-allowed theme-surface-muted' 
                      : 'theme-surface'
                  }`} style={{ borderColor: 'var(--border)' }}>
                    <input
                      type="radio"
                      name="accessLevel"
                      value="group_manager"
                      checked={currentAccessLevel === 'group_manager'}
                      onChange={() => handleAccessLevelChange('group_manager')}
                      disabled={!selectedGroup}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 disabled:cursor-not-allowed"
                    />
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div className="flex-1">
                      <div className="font-medium theme-text">Acesso por Grupo</div>
                      <div className="text-sm theme-text-muted">
                        {!selectedGroup ? 'Requer grupo associado' : 'Acesso a todas as empresas do grupo'}
                      </div>
                    </div>
                    {!selectedGroup && (
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                    )}
                  </label>
                </div>
              </div>

              {/* Seleção de Empresas */}
              {currentAccessLevel === 'company_manager' && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Building className="h-5 w-5 theme-text-muted" />
                    <h3 className="text-lg font-medium theme-text">Acesso por Empresa</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Lista de empresas específicas */}
                    {safeCompanies.map((company, index) => (
                      <label
                        key={company.id}
                        className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${
                          !selectedGroup && selectedCompanies.length >= 1 && !selectedCompanies.includes(company.id)
                            ? 'opacity-50 cursor-not-allowed theme-surface-muted'
                            : 'theme-surface'
                        }`}
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <input
                          type="checkbox"
                          checked={isCompanySelected(company.id)}
                          onChange={() => handleCompanyToggle(company.id)}
                          disabled={!selectedGroup && selectedCompanies.length >= 1 && !selectedCompanies.includes(company.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:cursor-not-allowed"
                        />
                        <div className={`w-3 h-3 ${getCompanyColor(index)} rounded-full`}></div>
                        <div className="flex-1">
                          <div className="font-medium theme-text">{company.name}</div>
                          {company.description && (
                            <p className="text-xs theme-text-muted">
                              {company.description}
                            </p>
                          )}
                        </div>
                        {isCompanySelected(company.id) && (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer com botões */}
          <div className="flex items-center justify-end gap-3 p-6 border-t theme-surface-muted" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors order-2 sm:order-1 theme-text"
              style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors order-1 sm:order-2"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{saving ? 'Salvando...' : 'Salvar'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notificação */}
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </>
  );
};

export default UserCompanyManagement;
